import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  updateCompanyDetails,
  createCompanyDetails,
  fetchCompanyDetails,
} from "../../store/company/actions"
import {
  selectCompanyDetails,
  selectCompanyLoading,
  selectCompanyUpdating,
} from "../../store/company/reducer"
import {
  Box,
  Grid,
  Typography,
  Button,
  Stack,
  Divider,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Collapse,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import PercentIcon from "@mui/icons-material/Percent"
import ReceiptIcon from "@mui/icons-material/Receipt"
import AccountTreeIcon from "@mui/icons-material/AccountTree"
import {
  getMyAadeCredentials,
  saveMyAadeCredentials,
  testMyAadeConnection,
  getExpenseCodes,
  postExpenseCode,
  patchExpenseCode,
  deleteExpenseCode,
  getVatCategories,
  createVatCategory,
  updateVatCategory,
  deleteVatCategory,
} from "../../helpers/api_helper"
import InvoiceCompaniesTab from "./InvoiceCompaniesTab"
import WorkflowEditorTab from "./WorkflowEditorTab"

function SubTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 1.5 }}>{children}</Box> : null
}

const CompanyTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const companyDetails = useSelector(selectCompanyDetails)
  const companyLoading = useSelector(selectCompanyLoading)
  const companyUpdating = useSelector(selectCompanyUpdating)

  const [subTab, setSubTab] = useState(0)
  const [companyOpen, setCompanyOpen] = useState(true)

  const [companyForm, setCompanyForm] = useState({
    name: "",
    vat: "",
    tax_office: "",
    address: "",
    city: "",
    postal_code: "",
    country: "",
    phone: "",
    email: "",
    website: "",
    bank_name: "",
    bank_account: "",
    bank_iban: "",
  })

  const [myAadeForm, setMyAadeForm] = useState({ username: "", password: "" })
  const [myAadeLoading, setMyAadeLoading] = useState(false)
  const [myAadeSaving, setMyAadeSaving] = useState(false)
  const [myAadeTesting, setMyAadeTesting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [myAadeConfigured, setMyAadeConfigured] = useState(false)

  // Expense Codes state
  const [expenseCodes, setExpenseCodes] = useState([])
  const [expenseCodesLoading, setExpenseCodesLoading] = useState(false)
  const [expenseCodeDialogOpen, setExpenseCodeDialogOpen] = useState(false)
  const [editingExpenseCode, setEditingExpenseCode] = useState(null)
  const [expenseCodeForm, setExpenseCodeForm] = useState({ name: "", code: "" })
  const [expenseCodeSaving, setExpenseCodeSaving] = useState(false)

  // VAT Categories state
  const [vatCategories, setVatCategories] = useState([])
  const [vatCategoriesLoading, setVatCategoriesLoading] = useState(false)
  const [vatCategoryDialogOpen, setVatCategoryDialogOpen] = useState(false)
  const [editingVatCategory, setEditingVatCategory] = useState(null)
  const [vatCategoryForm, setVatCategoryForm] = useState({ code: "", label: "", rate: "", is_intra_community: false })
  const [vatCategorySaving, setVatCategorySaving] = useState(false)

  useEffect(() => {
    dispatch(fetchCompanyDetails())
    loadMyAadeCredentials()
    fetchExpenseCodesData()
    fetchVatCategoriesData()
  }, [dispatch])

  const loadMyAadeCredentials = async () => {
    setMyAadeLoading(true)
    try {
      const response = await getMyAadeCredentials()
      if (response && response.configured) {
        setMyAadeConfigured(true)
        if (response.username) {
          setMyAadeForm(prev => ({ ...prev, username: response.username }))
        }
      }
    } catch (error) {
      console.error("Failed to load MyAade credentials:", error)
    } finally {
      setMyAadeLoading(false)
    }
  }

  useEffect(() => {
    if (companyDetails) {
      setCompanyForm({
        id: companyDetails.id || null,
        name: companyDetails.name || "",
        vat: companyDetails.vat || "",
        tax_office: companyDetails.tax_office || "",
        address: companyDetails.address || "",
        city: companyDetails.city || "",
        postal_code: companyDetails.postal_code || "",
        country: companyDetails.country || "",
        phone: companyDetails.phone || "",
        email: companyDetails.email || "",
        website: companyDetails.website || "",
        bank_name: companyDetails.bank_name || "",
        bank_account: companyDetails.bank_account || "",
        bank_iban: companyDetails.bank_iban || "",
      })
    }
  }, [companyDetails])

  const handleCompanyFormChange = field => e => {
    setCompanyForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleMyAadeFormChange = field => e => {
    setMyAadeForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSaveMyAadeCredentials = async () => {
    if (!myAadeForm.username.trim()) {
      onSnackbar?.({ open: true, message: t("MyAade username is required"), severity: "error" })
      return
    }
    if (!myAadeForm.password.trim() && !myAadeConfigured) {
      onSnackbar?.({ open: true, message: t("MyAade password is required"), severity: "error" })
      return
    }
    setMyAadeSaving(true)
    try {
      await saveMyAadeCredentials({
        username: myAadeForm.username.trim(),
        password: myAadeForm.password.trim() || undefined,
      })
      setMyAadeConfigured(true)
      setMyAadeForm(prev => ({ ...prev, password: "" }))
      onSnackbar?.({ open: true, message: t("MyAade credentials saved successfully"), severity: "success" })
    } catch (error) {
      onSnackbar?.({ open: true, message: t("Failed to save MyAade credentials"), severity: "error" })
    } finally {
      setMyAadeSaving(false)
    }
  }

  const handleTestMyAadeConnection = async () => {
    setMyAadeTesting(true)
    try {
      const result = await testMyAadeConnection()
      if (result && result.success) {
        onSnackbar?.({ open: true, message: t("MyAade connection successful"), severity: "success" })
      } else {
        onSnackbar?.({ open: true, message: t("MyAade connection failed"), severity: "error" })
      }
    } catch (error) {
      onSnackbar?.({ open: true, message: t("MyAade connection test failed"), severity: "error" })
    } finally {
      setMyAadeTesting(false)
    }
  }

  const handleSaveCompanyDetails = () => {
    if (!companyForm.name.trim()) {
      onSnackbar?.({ open: true, message: t("Company name is required"), severity: "error" })
      return
    }
    const data = {
      ...companyForm,
      name: companyForm.name.trim(),
      vat: companyForm.vat.trim(),
      tax_office: companyForm.tax_office.trim(),
      address: companyForm.address.trim(),
      city: companyForm.city.trim(),
      postal_code: companyForm.postal_code.trim(),
      country: companyForm.country.trim(),
      phone: companyForm.phone.trim(),
      email: companyForm.email.trim(),
      website: companyForm.website.trim(),
      bank_name: companyForm.bank_name.trim(),
      bank_account: companyForm.bank_account.trim(),
      bank_iban: companyForm.bank_iban.trim(),
    }
    if (companyForm.id) {
      dispatch(updateCompanyDetails(data))
    } else {
      dispatch(createCompanyDetails(data))
    }
    onSnackbar?.({ open: true, message: t("Company details saved successfully"), severity: "success" })
  }

  // Expense Codes handlers
  const fetchExpenseCodesData = async () => {
    setExpenseCodesLoading(true)
    try {
      const res = await getExpenseCodes().catch(() => [])
      setExpenseCodes(res || [])
    } catch (err) {
      console.error("Error fetching expense codes:", err)
    }
    setExpenseCodesLoading(false)
  }

  const handleOpenExpenseCodeDialog = (expenseCode = null) => {
    if (expenseCode) {
      setEditingExpenseCode(expenseCode)
      setExpenseCodeForm({ name: expenseCode.name || "", code: expenseCode.code || "" })
    } else {
      setEditingExpenseCode(null)
      setExpenseCodeForm({ name: "", code: "" })
    }
    setExpenseCodeDialogOpen(true)
  }

  const handleCloseExpenseCodeDialog = () => {
    setExpenseCodeDialogOpen(false)
    setEditingExpenseCode(null)
    setExpenseCodeForm({ name: "", code: "" })
  }

  const handleSaveExpenseCode = async () => {
    if (!expenseCodeForm.name.trim() || !expenseCodeForm.code.trim()) {
      onSnackbar?.({ open: true, message: t("Name and code are required"), severity: "error" })
      return
    }
    setExpenseCodeSaving(true)
    try {
      if (editingExpenseCode) {
        await patchExpenseCode({
          resource: [{ id: editingExpenseCode.id, name: expenseCodeForm.name.trim(), code: expenseCodeForm.code.trim() }],
        })
        onSnackbar?.({ open: true, message: t("Expense code updated successfully"), severity: "success" })
      } else {
        await postExpenseCode({
          resource: [{ name: expenseCodeForm.name.trim(), code: expenseCodeForm.code.trim() }],
        })
        onSnackbar?.({ open: true, message: t("Expense code added successfully"), severity: "success" })
      }
      handleCloseExpenseCodeDialog()
      fetchExpenseCodesData()
    } catch (err) {
      console.error("Error saving expense code:", err)
      onSnackbar?.({ open: true, message: t("Error saving expense code"), severity: "error" })
    }
    setExpenseCodeSaving(false)
  }

  const handleDeleteExpenseCode = async (expenseCode) => {
    if (!window.confirm(t("Are you sure you want to delete this expense code?"))) return
    try {
      await deleteExpenseCode({ resource: [{ id: expenseCode.id }] })
      onSnackbar?.({ open: true, message: t("Expense code deleted successfully"), severity: "success" })
      fetchExpenseCodesData()
    } catch (err) {
      console.error("Error deleting expense code:", err)
      onSnackbar?.({ open: true, message: t("Error deleting expense code"), severity: "error" })
    }
  }

  // VAT Categories handlers
  const fetchVatCategoriesData = async () => {
    setVatCategoriesLoading(true)
    try {
      const res = await getVatCategories().catch(() => [])
      setVatCategories(res || [])
    } catch (err) {
      console.error("Error fetching VAT categories:", err)
    }
    setVatCategoriesLoading(false)
  }

  const handleOpenVatCategoryDialog = (vatCategory = null) => {
    if (vatCategory) {
      setEditingVatCategory(vatCategory)
      setVatCategoryForm({
        code: vatCategory.code || "",
        label: vatCategory.label || "",
        rate: vatCategory.rate?.toString() || "",
        is_intra_community: vatCategory.is_intra_community || false,
      })
    } else {
      setEditingVatCategory(null)
      setVatCategoryForm({ code: "", label: "", rate: "", is_intra_community: false })
    }
    setVatCategoryDialogOpen(true)
  }

  const handleCloseVatCategoryDialog = () => {
    setVatCategoryDialogOpen(false)
    setEditingVatCategory(null)
    setVatCategoryForm({ code: "", label: "", rate: "", is_intra_community: false })
  }

  const handleSaveVatCategory = async () => {
    if (!vatCategoryForm.code.trim()) {
      onSnackbar?.({ open: true, message: t("Code is required"), severity: "error" })
      return
    }
    if (!vatCategoryForm.label.trim()) {
      onSnackbar?.({ open: true, message: t("Label is required"), severity: "error" })
      return
    }
    if (vatCategoryForm.rate === "" || isNaN(Number(vatCategoryForm.rate))) {
      onSnackbar?.({ open: true, message: t("Valid rate is required"), severity: "error" })
      return
    }
    setVatCategorySaving(true)
    try {
      const data = {
        code: vatCategoryForm.code.trim(),
        label: vatCategoryForm.label.trim(),
        rate: Number(vatCategoryForm.rate),
        is_intra_community: vatCategoryForm.is_intra_community,
      }
      if (editingVatCategory) {
        await updateVatCategory(editingVatCategory.id, data)
        onSnackbar?.({ open: true, message: t("VAT category updated successfully"), severity: "success" })
      } else {
        await createVatCategory(data)
        onSnackbar?.({ open: true, message: t("VAT category added successfully"), severity: "success" })
      }
      handleCloseVatCategoryDialog()
      fetchVatCategoriesData()
    } catch (err) {
      console.error("Error saving VAT category:", err)
      onSnackbar?.({ open: true, message: err.message || t("Error saving VAT category"), severity: "error" })
    }
    setVatCategorySaving(false)
  }

  const handleDeleteVatCategory = async (vatCategory) => {
    if (!window.confirm(t("Are you sure you want to delete this VAT category?"))) return
    try {
      await deleteVatCategory(vatCategory.id)
      onSnackbar?.({ open: true, message: t("VAT category deleted successfully"), severity: "success" })
      fetchVatCategoriesData()
    } catch (err) {
      console.error("Error deleting VAT category:", err)
      onSnackbar?.({ open: true, message: err.message || t("Error deleting VAT category"), severity: "error" })
    }
  }

  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }

  if (companyLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Company Information - Collapsible */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setCompanyOpen(!companyOpen)}
        sx={{ cursor: "pointer", py: 0.5, px: 0.5, borderRadius: 1, "&:hover": { bgcolor: "#f8fafc" } }}
      >
        <Typography variant="body2" fontWeight={600} color="#374151">
          {t("Basic Information")}
        </Typography>
        <IconButton size="small">
          {companyOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>
      <Collapse in={companyOpen}>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Company Name")} value={companyForm.name} onChange={handleCompanyFormChange("name")} required sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("VAT Number")} value={companyForm.vat} onChange={handleCompanyFormChange("vat")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Tax Office")} value={companyForm.tax_office} onChange={handleCompanyFormChange("tax_office")} sx={inputSx} />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mt: 1, mb: 0.5 }}>
            {t("Address")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label={t("Street Address")} value={companyForm.address} onChange={handleCompanyFormChange("address")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth size="small" label={t("City")} value={companyForm.city} onChange={handleCompanyFormChange("city")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth size="small" label={t("Postal Code")} value={companyForm.postal_code} onChange={handleCompanyFormChange("postal_code")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth size="small" label={t("Country")} value={companyForm.country} onChange={handleCompanyFormChange("country")} sx={inputSx} />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mt: 1, mb: 0.5 }}>
            {t("Contact")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Phone")} value={companyForm.phone} onChange={handleCompanyFormChange("phone")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Email")} type="email" value={companyForm.email} onChange={handleCompanyFormChange("email")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Website")} value={companyForm.website} onChange={handleCompanyFormChange("website")} sx={inputSx} />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mt: 1, mb: 0.5 }}>
            {t("Bank Information")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Bank Name")} value={companyForm.bank_name} onChange={handleCompanyFormChange("bank_name")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("Bank Account")} value={companyForm.bank_account} onChange={handleCompanyFormChange("bank_account")} sx={inputSx} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label={t("IBAN")} value={companyForm.bank_iban} onChange={handleCompanyFormChange("bank_iban")} sx={inputSx} />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              startIcon={companyUpdating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
              onClick={handleSaveCompanyDetails}
              disabled={companyUpdating}
              sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, borderRadius: 1.5, textTransform: "none", fontWeight: 500 }}
            >
              {t("Save Company")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
      </Collapse>

      {/* Sub-tabs for related settings */}
      <Divider sx={{ mt: 2, mb: 0 }} />
      <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
        <Tabs
          value={subTab}
          onChange={(e, v) => setSubTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 32,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.7rem",
              minHeight: 30,
              py: 0.25,
              px: 1,
              borderRadius: 1,
              mx: 0.25,
              color: "#64748b",
            },
            "& .Mui-selected": {
              color: "#fff !important",
              bgcolor: "#d97706",
              fontWeight: 600,
            },
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <Tab icon={<VisibilityIcon sx={{ fontSize: 14 }} />} iconPosition="start" label={t("MyAade")} />
          <Tab icon={<PercentIcon sx={{ fontSize: 14 }} />} iconPosition="start" label={t("VAT Categories")} />
          <Tab icon={<ReceiptIcon sx={{ fontSize: 14 }} />} iconPosition="start" label={t("Invoice Companies")} />
          <Tab icon={<AccountTreeIcon sx={{ fontSize: 14 }} />} iconPosition="start" label={t("Workflow")} />
          <Tab icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />} iconPosition="start" label={t("Expense Codes")} />
        </Tabs>
      </Box>

      {/* MyAade Sub-tab */}
      <SubTabPanel value={subTab} index={0}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#374151">
            {t("MyAade Integration")}
          </Typography>
          {myAadeConfigured && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
              label={t("Configured")}
              size="small"
              sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 500, fontSize: "0.65rem", height: 20 }}
            />
          )}
        </Stack>

        {myAadeLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 1.5, py: 0, fontSize: "0.75rem" }}>
                {t("These credentials are used to validate VAT numbers through the AADE (Greek Tax Authority) service.")}
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label={t("MyAade Username")} value={myAadeForm.username} onChange={handleMyAadeFormChange("username")} sx={inputSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? "text" : "password"}
                label={myAadeConfigured ? t("Password (leave empty to keep)") : t("MyAade Password")}
                value={myAadeForm.password}
                onChange={handleMyAadeFormChange("password")}
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                {myAadeConfigured && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleTestMyAadeConnection}
                    disabled={myAadeTesting}
                    startIcon={myAadeTesting ? <CircularProgress size={14} /> : null}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 500, borderColor: "#e5e7eb", color: "#374151" }}
                  >
                    {t("Test Connection")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={myAadeSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                  onClick={handleSaveMyAadeCredentials}
                  disabled={myAadeSaving}
                  sx={{ bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" }, borderRadius: 1.5, textTransform: "none", fontWeight: 500 }}
                >
                  {t("Save Credentials")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        )}
      </SubTabPanel>

      {/* VAT Categories Sub-tab */}
      <SubTabPanel value={subTab} index={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#374151">
            {t("VAT Categories")}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleOpenVatCategoryDialog()}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, borderRadius: 1.5, textTransform: "none", fontWeight: 500, fontSize: "0.75rem" }}
          >
            {t("Add")}
          </Button>
        </Stack>

        {vatCategoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Code")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Label")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Rate")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Intra-Community")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vatCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#6b7280", fontSize: "0.75rem" }}>
                      {t("No VAT categories found")}
                    </TableCell>
                  </TableRow>
                ) : (
                  vatCategories.map((vat) => (
                    <TableRow key={vat.id} hover>
                      <TableCell>
                        <Chip label={vat.code} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize="0.8rem" fontWeight={500}>{vat.label}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${vat.rate}%`} size="small" sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vat.is_intra_community ? t("Yes") : t("No")}
                          size="small"
                          sx={{ bgcolor: vat.is_intra_community ? "#dbeafe" : "#f3f4f6", color: vat.is_intra_community ? "#3b82f6" : "#6b7280", fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenVatCategoryDialog(vat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteVatCategory(vat)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SubTabPanel>

      {/* Invoice Companies Sub-tab */}
      <SubTabPanel value={subTab} index={2}>
        <InvoiceCompaniesTab onSnackbar={onSnackbar} />
      </SubTabPanel>

      {/* Workflow Sub-tab */}
      <SubTabPanel value={subTab} index={3}>
        <WorkflowEditorTab onSnackbar={onSnackbar} />
      </SubTabPanel>

      {/* Expense Codes Sub-tab */}
      <SubTabPanel value={subTab} index={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#374151">
            {t("Expense Codes")}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleOpenExpenseCodeDialog()}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, borderRadius: 1.5, textTransform: "none", fontWeight: 500, fontSize: "0.75rem" }}
          >
            {t("Add")}
          </Button>
        </Stack>

        {expenseCodesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Code")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Name")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenseCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3, color: "#6b7280", fontSize: "0.75rem" }}>
                      {t("No expense codes found")}
                    </TableCell>
                  </TableRow>
                ) : (
                  expenseCodes.map((code) => (
                    <TableRow key={code.id} hover>
                      <TableCell>
                        <Chip label={code.code} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{code.name}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenExpenseCodeDialog(code)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteExpenseCode(code)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SubTabPanel>

      {/* Expense Code Dialog */}
      <Dialog open={expenseCodeDialogOpen} onClose={handleCloseExpenseCodeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          {editingExpenseCode ? t("Edit Expense Code") : t("Add Expense Code")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth size="small" label={t("Code")} value={expenseCodeForm.code}
              onChange={(e) => setExpenseCodeForm(prev => ({ ...prev, code: e.target.value }))}
              required sx={inputSx}
            />
            <TextField
              fullWidth size="small" label={t("Name")} value={expenseCodeForm.name}
              onChange={(e) => setExpenseCodeForm(prev => ({ ...prev, name: e.target.value }))}
              required sx={inputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={handleCloseExpenseCodeDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained" onClick={handleSaveExpenseCode} disabled={expenseCodeSaving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {expenseCodeSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VAT Category Dialog */}
      <Dialog open={vatCategoryDialogOpen} onClose={handleCloseVatCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          {editingVatCategory ? t("Edit VAT Category") : t("Add VAT Category")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth size="small" label={t("Code")} value={vatCategoryForm.code}
              onChange={(e) => setVatCategoryForm(prev => ({ ...prev, code: e.target.value }))}
              required placeholder="e.g., VAT24, VAT13, VAT0" sx={inputSx}
            />
            <TextField
              fullWidth size="small" label={t("Label")} value={vatCategoryForm.label}
              onChange={(e) => setVatCategoryForm(prev => ({ ...prev, label: e.target.value }))}
              required placeholder="e.g., Standard Rate, Reduced Rate" sx={inputSx}
            />
            <TextField
              fullWidth size="small" label={t("Rate (%)")} type="number" value={vatCategoryForm.rate}
              onChange={(e) => setVatCategoryForm(prev => ({ ...prev, rate: e.target.value }))}
              required inputProps={{ min: 0, max: 100, step: 0.01 }}
              placeholder="e.g., 24, 13, 6, 0" sx={inputSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={vatCategoryForm.is_intra_community}
                  onChange={(e) => setVatCategoryForm(prev => ({ ...prev, is_intra_community: e.target.checked }))}
                  sx={{ color: "#3b82f6", "&.Mui-checked": { color: "#3b82f6" } }}
                />
              }
              label={t("Intra-Community Transaction")}
              sx={{ "& .MuiFormControlLabel-label": { color: "#374151" } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={handleCloseVatCategoryDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained" onClick={handleSaveVatCategory} disabled={vatCategorySaving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {vatCategorySaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CompanyTab
